import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import "./App.css";

// Notification System
import { NotificationProvider } from "./context/NotificationContext";
import UserNotificationHandler from "./components/notifications/UserNotificationHandler";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Auth Guard
import ProtectedRoute from "./components/routes/ProtectedRoute";

// Layout + Auth
import UserLayout from "./components/layout/UserLayout";

// Public pages
import Login from "./pages/login/Login";
import Register from "./pages/login/Register";
import ForgotPassword from "./pages/passwordsection/components/ForgotPassword";
import NotFound from "./pages/404NotFound/NotFound";
import ScrollToTop from "./components/scrollToTop/ScrollToTop";

// Pages inside layout
import Home from "./pages/home/Home";
import CategoryDetails from "./pages/home/sections/category/CategoryDetails";
import ServiceDetails from "./pages/home/sections/services/ServiceDetails";
import ExpertsListHome from "./pages/experts-list/ExpertsListHome";
import PickSlot from "./pages/pickSlots/PickSlot";
import VendorDetails from "./pages/home/sections/recommendedVendor/VendorDetails";
import AllServicesPage from "./pages/home/sections/services/AllServicesPage.js";
import AllCategoriesPage from "./pages/home/sections/category/AllCategoriesPage";
import About from "./pages/about/About";
import Contact from "./pages/contact/Contact";
import FAQ from "./pages/faq/FAQ";
import SearchResultsPage from './pages/SearchResults/SearchResultsPage';
import NotificationsPage from "./pages/notifications/NotificationsPage.js";
import BookingDetails from "./pages/profile/bookings/components/BookingDetails";
import ProfileSection from "./pages/profilesection/ProfileSection";
import PasswordSections from "./pages/passwordsection/PasswordSections";
import HelpSection from "./pages/helpsection/HelpSection";
import BookingSection from "./pages/bookings/BookingSection";
import VideoCall from "./pages/video/VideoCall.js";
import UserIncomingCall from "./pages/video/UserIncomingCall";
import Privacy from "./pages/privacy/Privacy.js";
import Terms from "./pages/terms/Terms.js";
import ResetPassword from "./pages/passwordsection/components/ResetPassword";

function App() {
    const GOOGLE_CLIENT_ID = "90871662580-t48tsvr2oh2f4i8rbril9n66cghfciiv.apps.googleusercontent.com";
    const userId = localStorage.getItem("userId");

    useEffect(() => {
        const handleGlobalAuth = () => {
            const currentUserId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
            const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");
            const publicPaths = ["/", "/signup", "/forgot-password"];
            const isPublicPath = publicPaths.includes(window.location.pathname) || 
                               window.location.pathname.startsWith("/reset-password");

            if ((!currentUserId || !currentToken) && !isPublicPath) {
                localStorage.removeItem("token");
                localStorage.removeItem("userId");
                sessionStorage.removeItem("token");
                sessionStorage.removeItem("userId");
                window.location.replace("/"); 
            }
        };

        window.addEventListener("popstate", handleGlobalAuth);
        window.addEventListener("authChange", handleGlobalAuth);
        window.addEventListener("storage", handleGlobalAuth);

        return () => {
            window.removeEventListener("popstate", handleGlobalAuth);
            window.removeEventListener("authChange", handleGlobalAuth);
            window.removeEventListener("storage", handleGlobalAuth);
        };
    }, []);

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID} use_fedcm={false}>
        <Router>
            <NotificationProvider>
                <UserNotificationHandler userId={userId} />

                <div className="App">
                    <ScrollToTop />
                    <ToastContainer position="top-right" autoClose={3000} />

                    <Routes>
                        {/* PUBLIC ROUTES */}
                        <Route path="/" element={<Login />} />
                        <Route path="/signup" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />

                        {/* PROTECTED ROUTES (WRAPPED IN LAYOUT) */}
                        <Route element={
                            <ProtectedRoute>
                                <UserLayout />
                            </ProtectedRoute>
                        }>
                            <Route path="/home" element={<Home />} />
                            <Route path="/category/:id" element={<CategoryDetails />} />
                            <Route path="/services/:serviceId" element={<ServiceDetails />} />
                            <Route path="/vendor-details/:vendorId" element={<VendorDetails />} />
                            <Route path="/book-appointment/:serviceTitle" element={<PickSlot />} />
                            <Route path="/pick-slot" element={<PickSlot />} />
                            <Route path="/services" element={<AllServicesPage />} />
                            <Route path="/categories" element={<AllCategoriesPage />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/our-experts" element={<ExpertsListHome />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/search-results" element={<SearchResultsPage />} />
                            <Route path="/booking" element={<BookingSection />} />
                            <Route path="/profile-section" element={<ProfileSection />} />
                            <Route path="/password" element={<PasswordSections />} />
                            <Route path="/help" element={<HelpSection />} />
                            <Route path="/help-section" element={<HelpSection />} />
                            <Route path="/profile/my-profile" element={<ProfileSection />} />
                            <Route path="/profile/my-profile/edit-profile" element={<ProfileSection />} />
                            <Route path="/profile/my-bookings" element={<BookingSection />} />
                            <Route path="/profile/my-bookings/:bookingId" element={<BookingDetails />} />
                            <Route path="/profile/help" element={<HelpSection />} />
                            <Route path="/profile/password-setting" element={<PasswordSections />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                        </Route>

                        {/* FULLSCREEN STANDALONE CALL ROUTES (NO NAVBAR/FOOTER OVERLAP) */}
                        <Route path="/video/:bookingId" element={
                            <ProtectedRoute>
                                <VideoCall />
                            </ProtectedRoute>
                        } />
                        <Route path="/user/call/:bookingId" element={
                            <ProtectedRoute>
                                <UserIncomingCall />
                            </ProtectedRoute>
                        } />

                        {/* 404 FALLBACK */}
                        <Route path="/*" element={<NotFound />} />
                    </Routes>
                </div>
            </NotificationProvider>
        </Router>
        </GoogleOAuthProvider>
    );
}

export default App;